import { configureStore, createListenerMiddleware } from '@reduxjs/toolkit';
import { persistStore, persistReducer, createMigrate, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { logoutUser } from '@/features/user/userSlice';
import { hubApi } from './api/hubApi';
import { resourceApi } from './api/resourceApi';
import { saleApi } from './api/saleApi';
import { loyaltyApi } from '@/features/loyalty/loyaltyApi';
import { apiMiddleware, rootReducer, withLogoutReset } from './rootReducer';
import { cartPersistence } from './cartPersistence';

// v4 — cart.modeByRestaurant replaced the single global cart.ui.mode. A v3
// blob has no modeByRestaurant, which would crash selectCartMode on rehydrate.
// Initialize the missing map (and drop the obsolete ui.mode) while keeping
// every line already in the user's cart.
const cartV3ToV4 = (state: any) => {
  if (!state) return state;
  const cart = state.cart ?? {};
  return {
    ...state,
    cart: {
      byRestaurant: cart.byRestaurant ?? {},
      modeByRestaurant: cart.modeByRestaurant ?? {},
      ui: { isDrawerOpen: cart.ui?.isDrawerOpen ?? false },
    },
  };
};

// v5 — SelectedOption shape changed from { groupId, optionIds: string[] } to
// { groupId, options: { id, qty }[] }. Migrate every persisted cart line's
// selectedOptions so old localStorage carts load cleanly.
function migrateLegacySelectedOption(raw: unknown): { groupId: string; options: { id: string; qty: number }[] } {
  const obj = (raw ?? {}) as { groupId?: string; optionIds?: unknown; options?: unknown };
  if (Array.isArray(obj.options)) {
    return {
      groupId: String(obj.groupId ?? ''),
      options: (obj.options as { id: string; qty: number }[]).map((o) => ({
        id: String(o.id),
        qty: Math.max(1, Number(o.qty) || 1),
      })),
    };
  }
  const ids = Array.isArray(obj.optionIds) ? (obj.optionIds as string[]) : [];
  const counts = new Map<string, number>();
  for (const id of ids) counts.set(id, (counts.get(id) ?? 0) + 1);
  return {
    groupId: String(obj.groupId ?? ''),
    options: [...counts].map(([id, qty]) => ({ id, qty })),
  };
}

const cartV4ToV5 = (state: any) => {
  if (!state) return state;
  const cart = state.cart ?? {};
  const byRestaurant: Record<string, unknown[]> = cart.byRestaurant ?? {};
  const migratedByRestaurant: Record<string, unknown[]> = {};
  for (const [uuid, lines] of Object.entries(byRestaurant)) {
    migratedByRestaurant[uuid] = (lines as any[]).map((line: any) => ({
      ...line,
      selectedOptions: Array.isArray(line.selectedOptions)
        ? line.selectedOptions.map(migrateLegacySelectedOption)
        : [],
    }));
  }
  return {
    ...state,
    cart: {
      ...cart,
      byRestaurant: migratedByRestaurant,
    },
  };
};

// v6 — OrderMode union gains 'dine_in'. tableSession is NOT persisted via
// redux-persist (it lives in localStorage keyed by restaurantUuid and is
// rehydrated by the slice itself). No structural state migration needed here;
// version bump signals the new OrderMode value to any persisted blobs.
const cartV5ToV6 = (state: any) => state;

// v7 — SC-1092 : un mode sans panier ne compte plus comme choisi. Avant, le
// mode d'une commande deja passee restait en memoire et la carte s'ouvrait a
// ses prix sans rien demander ; on l'oublie pour tout restaurant dont le panier
// est vide, et la question « A emporter ou Livraison ? » est reposee.
export const cartV6ToV7 = (state: any) => {
  if (!state?.cart?.modeByRestaurant) return state;
  const byRestaurant: Record<string, unknown[]> = state.cart.byRestaurant ?? {};
  const modeByRestaurant: Record<string, unknown> = {};
  for (const [uuid, mode] of Object.entries(state.cart.modeByRestaurant)) {
    if ((byRestaurant[uuid] ?? []).length > 0) modeByRestaurant[uuid] = mode;
  }
  return { ...state, cart: { ...state.cart, modeByRestaurant } };
};

export const persistConfig = {
  key: 'site-commande-root',
  storage,
  version: 7,
  transforms: [cartPersistence],
  migrate: createMigrate({ 4: cartV3ToV4, 5: cartV4ToV5, 6: cartV5ToV6, 7: cartV6ToV7 } as never, { debug: false }),
  // tableSession is intentionally absent from this whitelist — it persists
  // itself to localStorage (survives page reload and a QR rescan in a new tab,
  // DF-0259; still wiped on logout and when the session expires).
  whitelist: ['cart', 'user'],
};

const persistedReducer = persistReducer<RootState>(persistConfig, rootReducer);

// Re-exported so the historical `import { withLogoutReset } from '@/app/store'`
// keeps working; it lives next to the reducer map it depends on.
export { withLogoutReset };

const isolatedReducer = withLogoutReset(persistedReducer);

// On logoutUser: clear each API's cached queries/mutations the official way.
// `resetApiState()` empties queries+mutations but keeps config.middlewareRegistered
// and lets mounted hooks re-subscribe — exactly what we want here.
const logoutListener = createListenerMiddleware();
logoutListener.startListening({
  actionCreator: logoutUser,
  effect: (_, api) => {
    api.dispatch(hubApi.util.resetApiState());
    api.dispatch(resourceApi.util.resetApiState());
    api.dispatch(saleApi.util.resetApiState());
    api.dispatch(loyaltyApi.util.resetApiState());
  },
});

export const store = configureStore({
  reducer: isolatedReducer,
  middleware: (getDefault) =>
    getDefault({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .prepend(logoutListener.middleware)
      .concat(...apiMiddleware),
});

export const persistor = persistStore(store);

// Defense in depth: even though the reducer wrapper has already reset
// in-memory state, also purge the persisted blob in localStorage so a
// reload after logout cannot rehydrate the previous consumer's data.
// `lastAuthStatus` MUST be updated before calling `persistor.purge()` —
// purge dispatches PURGE synchronously which re-fires this subscriber,
// and a stale guard here would loop until the call stack overflows.
let lastAuthStatus: string | undefined = store.getState().user.status;
store.subscribe(() => {
  const status = store.getState().user.status;
  const wasAuthenticated = lastAuthStatus === 'authenticated';
  lastAuthStatus = status;
  if (wasAuthenticated && status === 'idle') {
    persistor.purge();
  }
});

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
